import Moment from 'moment';
import {
  applyQueryConfig,
  responseStringTransformer,
  ServiceHandler,
} from '../utils';
import {
  standardParamSerializer,
  standardQuerySerializer,
} from './serializers';
import { cmailAccountsParamsSerializer } from './cmail/cmail-accounts.params-serializer';
import type { CmailMessage } from './cmail/cmail.dto';
import { commonDateParamMappings } from './common/mappings';
import { normalizeMessageCategoryForUI } from '../console/cmail/utils/emailTypeUtils';

export const transformMessage = (
  message: CmailMessage,
  emailDirection?: 'SENT' | 'INBOX'
) => {
  // Calculate emailReferenceCount based on childCount or references array
  let emailReferenceCount;

  // First check if childCount is available and greater than 0
  if (message.childCount && message.childCount > 0) {
    emailReferenceCount = message.childCount + 1;
  } else if (
    message.references &&
    Array.isArray(message.references) &&
    message.references.length > 0
  ) {
    // Fallback to existing references logic
    emailReferenceCount = message.references.length + 1;
  }

  return {
    ...message,
    msgCategory: normalizeMessageCategoryForUI(message.msgCategory),
    ui_dateReceivedShort: Moment.utc(message.dateReceived).format('MMM D'),
    ui_dateReceivedLong: `${Moment.utc(message.dateReceived).format(
      'LLLL'
    )} (${Moment.utc(message.dateReceived).fromNow()})`,
    seenBy: message.seenBy ?? [],
    isArchived: message.isArchived ?? false,
    isStarred: message.isStarred ?? false,
    labels: message.labels ?? [],
    label: message.label,
    ...(emailReferenceCount && { emailReferenceCount }),
    ...(emailDirection && { emailDirection }),
  };
};

export const getMessageByMsgId = ServiceHandler({
  url: '/api/docmessage/v1/message',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
  transformResponse: [responseStringTransformer],
});

export const getLatestMessage = ServiceHandler({
  url: '/api/docmessage/v1/latest/message',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
  transformResponse: [responseStringTransformer],
});

/** A cmail message, as docmessage's `GET /v1/messages` lists them. */
export interface EmailMessage {
  readonly msgId: string;
  /** 'SENT' | 'DRAFT' | other */
  readonly direction: string;
  readonly subject: string | null;
  readonly senderName: string | null;
  readonly senderEmail: string | null;
  readonly to: readonly string[];
  readonly cc: readonly string[];
  /** ISO date */
  readonly sentAt: string | null;
  readonly htmlBody: string | null;
  readonly textBody: string | null;
  /** The MessageCategory name, e.g. 'CLAIMS'. */
  readonly category?: string | null;
  /** The automated template that produced the message, e.g. 'Claim Acknowledgment'. */
  readonly automationTemplate?: string | null;
}

/** What `getMessages` filters on. `replyToMsgId` or `claimId` is required; the rest narrow the result. */
export interface MessageFilters {
  readonly replyToMsgId?: string;
  readonly claimId?: string;
  /** EmailDirection name: 'SENT', 'DRAFT', 'INBOX'. */
  readonly direction?: string;
  /** MessageCategory name, e.g. 'CLAIMS'. */
  readonly category?: string;
  readonly automationTemplate?: string;
}

/** The messages matching every filter given, oldest first. Replies are `replyToMsgId`. */
export const getMessages = ServiceHandler<unknown, readonly EmailMessage[]>({
  url: '/api/docmessage/v1/messages',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(
        [
          { path: 'replyToMsgId' },
          { path: 'claimId' },
          { path: 'direction' },
          { path: 'category' },
          { path: 'automationTemplate' },
        ],
        params
      )
    ),
});

export const getLatestDraft = ServiceHandler({
  url: '/api/docmessage/v1/latest/draft',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
  transformResponse: [responseStringTransformer],
});

const cmailSearchParamsSerializer = [
  ...commonDateParamMappings,
  ...standardParamSerializer,
  { path: 'emailDirection' },
];

const cmailAccountSearchParamsSerializer = [
  ...commonDateParamMappings,
  ...cmailAccountsParamsSerializer,
];

export const getAllMessages = ServiceHandler({
  url: '/api/docmessage/v1/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(cmailSearchParamsSerializer, params)
    ),
  transformResponse: [responseStringTransformer],
});

export const updateCmailMessage = ServiceHandler({
  url: '/api/docmessage/v1/message',
  method: 'PUT',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
});

export const getMessagesByAccountIdAndAccountName = ServiceHandler({
  url: '/api/docmessage/v1/searchForAccount',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(cmailAccountSearchParamsSerializer, params)
    ),
});

export type MessageInfoLabel = {
  label: string;
  value: string;
};

export const getLabels = ServiceHandler<unknown, MessageInfoLabel[]>({
  url: '/api/docmessage/v1/labels',
});

export const updateLabel = ServiceHandler({
  url: '/api/docmessage/v1/:msgId/label',
  method: 'PUT',
});

export interface AssignMessagePayload {
  email: string;
  firstName?: string;
  lastName?: string;
}

export const assignMessage = ServiceHandler<AssignMessagePayload>({
  url: '/api/docmessage/v1/message/assign',
  method: 'POST',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
  transformResponse: [responseStringTransformer],
});

export interface SendEmailPayload {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  htmlBody?: string;
  attachments?: Array<{
    docId: string;
    docName: string;
    docContentType: string;
    docSize: string;
  }>;
  from?: string;
  senderName?: string;
  msgId?: string;
  references?: string[];
  replyToMsgId?: string;
  emailType?: 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';
  msgCategory?: string;
  /** Where a reply goes, where it isn't the sender: sent as the `Reply-To` header. Absent, nothing changes. */
  replyTo?: string[];
}

export interface SaveDraftPayload {
  to?: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  htmlBody?: string;
  attachments?: Array<{
    docId: string;
    docName: string;
    docContentType: string;
    docSize: string;
  }>;
  from?: string;
  senderName?: string;
  msgId?: string;
  references?: string[];
  replyToMsgId?: string;
  emailType?: 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';
  msgCategory?: string;
  /** Where a reply goes, where it isn't the sender: sent as the `Reply-To` header. Absent, nothing changes. */
  replyTo?: string[];
}

export interface AISuggestPayload {
  subject: string;
  body: string;
  mode: 'initial' | 'refine';
  emailType: 'NEW' | 'REPLY' | 'FORWARD';
  refine?: {
    description: string;
    tone: string;
    missingFields?: {
      description: string;
      fields: Array<{
        name: string;
        group: string;
      }>;
      totalCount: number;
    };
  };
  msgCategory: string;
  to: string;
}

export interface AISuggestResponse {
  suggestions: string[];
}

export const sendEmail = ServiceHandler<SendEmailPayload>({
  url: '/api/docmessage/v1/send',
  method: 'POST',
});

export const aiSuggest = ServiceHandler<AISuggestPayload, AISuggestResponse>({
  url: '/api/docmessage/v1/ai-suggest/suggest',
  method: 'POST',
});

export const saveDraft = ServiceHandler<SaveDraftPayload>({
  url: '/api/docmessage/v1/draft',
  method: 'POST',
});

export interface ReprocessEmailPayload {
  msgId: string;
  category?: string;
  subCategory?: string;
}

export const reprocessEmail = ServiceHandler<ReprocessEmailPayload>({
  url: '/api/docmessage/v1/reprocess',
  method: 'PUT',
});

export interface DeleteMessagesPayload {
  msgIds: string[];
}

export const deleteMessages = ServiceHandler<string[]>({
  url: '/api/docmessage/v1/messages',
  method: 'DELETE',
});

export interface DraftStats {
  lastUpdated: string;
  categoryStats: Record<string, number>;
  totalDrafts: number;
}

export const getDraftStats = ServiceHandler<unknown, DraftStats>({
  url: '/api/docmessage/v1/draft-stats',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig([{ path: 'before' }, { path: 'after' }], params)
    ),
});

export interface UploadAttachmentResponse {
  docId: string;
  docType: string;
  docContentType: string;
  docName: string;
  docSize: string;
  docTime: string;
  s3Path: string;
  bucketName: string;
  signedS3Url: string;
}

// Request Tracking API interfaces
export interface RequestTrackingItem {
  itemId: string | null;
  itemType: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'ESCALATED';
  failureReason: string | null;
  failureStage: string | null;
  processedAt: string | null;
  metadata: Record<string, any>;
}

export interface RequestTrackingData {
  id: string;
  msgId: string;
  msgCategory: string | null;
  requestStatus: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'ESCALATED';
  totalItems: number;
  completedItems: number;
  failedItems: number;
  escalatedItems: number;
  itemTrackings: RequestTrackingItem[];
  requestTimestamp: string;
  lastUpdateTimestamp: string;
}

export const getRequestTracking = ServiceHandler<RequestTrackingData>({
  url: '/api/docmessage/v1/request-tracking/:msgId',
  transformResponse: [responseStringTransformer],
});

export const uploadAttachment = ServiceHandler<
  FormData,
  UploadAttachmentResponse
>({
  url: '/api/docmessage/v1/message/:msgId/attachment',
  method: 'POST',
});

export interface AttachmentFromUrlPayload {
  signedS3Url: string;
  docType: string;
  fileName: string;
}

export const uploadAttachmentFromUrl = ServiceHandler<
  AttachmentFromUrlPayload,
  UploadAttachmentResponse
>({
  url: '/api/docmessage/v1/message/:msgId/attachment-from-url',
  method: 'POST',
});

export interface MessageActivity {
  id: string;
  msgId: string;
  name: string;
  displayName: string;
  isDisplay: boolean;
  eventOrder: number;
  startTime: string;
  endTime: string | null;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'FAILED';
  metadata: string | null;
  continueOnError: boolean;
  isReprocessable: boolean;
  errorMessage: string | null;
  retryCount: number;
  parentActivityName: string | null;
}

export const getMessageActivity = ServiceHandler<MessageActivity[]>({
  url: '/api/docmessage/v1/message-activities',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'msgId' }], params)),
});

export const approveMessage = ServiceHandler<void>({
  url: '/api/docmessage/v1/approve-message/:msgId',
  method: 'POST',
});
